/**
 * One set of rules for rendering the mobile plan catalogue.
 *
 * Three screens draw this list — the portal lock screen, the portal Mobile Plan page and the
 * public site's plan pickers — and each had grown its own copy of "is this free?" and "how do
 * I print a price?". They disagreed: one printed `£59.99/3 months`, another `£59.99 / 3 months`,
 * and only one of the three hid a free trial the user had already used, so the other two showed
 * a Start free trial button that failed when pressed.
 *
 * Kept deliberately free of React and MUI so the public site can hold a byte-identical copy
 * (the two apps are separate CRA builds and cannot import across).
 */

/** Free trial, free tier — anything we must not send to Stripe Checkout. */
export const isFreePlan = (plan) =>
  Number(plan?.price_pence || 0) === 0 ||
  String(plan?.slug || "").startsWith("free-trial") ||
  String(plan?.name || "").toLowerCase().includes("free");

/**
 * What this viewer would actually be charged.
 *
 * `offer_price_pence` is the API's answer for this specific user — the early-adopter price
 * while that window is open, the catalogue price otherwise. Falls back to `price_pence` so an
 * older API response still renders.
 */
export const planAmountPence = (plan) => {
  const offer = plan?.offer_price_pence;
  return Number(offer === undefined || offer === null ? plan?.price_pence || 0 : offer);
};

/** True when this plan is being offered below its list price. */
export const hasPromoPrice = (plan) =>
  Boolean(plan?.is_promo_price) && planAmountPence(plan) < Number(plan?.price_pence || 0);

export const formatMoney = (pence, currency = "gbp") => {
  const code = String(currency || "gbp").toUpperCase();
  const symbol = code === "GBP" ? "£" : `${code} `;
  return `${symbol}${(Number(pence || 0) / 100).toFixed(2)}`;
};

/**
 * The "/mo" part of a price.
 *
 * Multi-month intervals are the reason this exists: the old helper had no case for them and
 * fell through to `/${interval}`, printing "£59.99/3 months".
 */
export const formatBillingSuffix = (interval) => {
  const normalized = String(interval || "").trim().toLowerCase();
  switch (normalized) {
    case "day":
    case "1 day":
      return "/day";
    case "week":
    case "1 week":
      return "/wk";
    case "month":
    case "1 month":
      return "/mo";
    case "3 months":
    case "quarter":
      return "/quarter";
    case "6 months":
      return "/6 mo";
    case "year":
    case "1 year":
    case "yearly":
    case "annual":
      return "/yr";
    default:
      return normalized ? `/${normalized}` : "";
  }
};

/**
 * The standardised billing vocabulary: Weekly, Monthly, Quarterly, Semi-Annual, Annual.
 *
 * Aligned in the 22 Sep 2026 review — these are the words customers already recognise from
 * every other subscription they hold, so the duration should never be spelled a new way.
 */
export const planDurationLabel = (plan) => {
  const normalized = String(plan?.billing_interval || "").trim().toLowerCase();
  switch (normalized) {
    case "day":
    case "1 day":
      return "Daily";
    case "week":
    case "1 week":
      return "Weekly";
    case "month":
    case "1 month":
      return "Monthly";
    case "3 months":
    case "quarter":
      return "Quarterly";
    case "6 months":
      return "Semi-Annual";
    case "year":
    case "1 year":
    case "yearly":
    case "annual":
      return "Annual (12 months)";
    default:
      return plan?.name || "";
  }
};

/** "£29.99/mo", or "Free trial" for the free tier. */
export const formatPlanPrice = (plan) => {
  if (!plan) return "-";
  if (isFreePlan(plan)) return "Free trial";
  return `${formatMoney(planAmountPence(plan), plan.currency)}${formatBillingSuffix(plan.billing_interval)}`;
};

/** The struck-through list price to show beside a promo, or null when there is no discount. */
export const formatListPrice = (plan) => {
  if (!plan || !hasPromoPrice(plan)) return null;
  return `${formatMoney(plan.price_pence, plan.currency)}${formatBillingSuffix(plan.billing_interval)}`;
};

/** Months a billing interval covers, for per-month comparisons. 0 when it is not monthly-ish. */
export const monthsInInterval = (interval) => {
  const normalized = String(interval || "").trim().toLowerCase();
  if (normalized === "month" || normalized === "1 month") return 1;
  if (normalized === "year" || normalized === "1 year" || normalized === "yearly" || normalized === "annual") return 12;
  const months = /^(\d+)\s*months?$/.exec(normalized);
  return months ? Number(months[1]) : 0;
};

/**
 * Which plans this viewer may choose.
 *
 * The rules, all from the 22 Sep 2026 review:
 *
 *  - A free trial that has already been consumed is not an option. Rendering it anyway gave
 *    the user a button that errored, which is worse than not offering it.
 *  - Someone on a paid plan moves up, not down. Changing to a cheaper plan mid-term is a
 *    refund question, not a self-service one, so a downgrade means cancelling first. The
 *    parent panel has always worked this way; the student screens did not.
 *  - A returning customer has a floor. After a cancellation that floor is the Monthly plan:
 *    the free trial and the £1 weekly taster are acquisition offers, and someone who has
 *    already held a paid plan should not be able to drop back onto them. After a plan simply
 *    expired the floor is the plan they were on, so renewing or upgrading is offered but
 *    downgrading is not.
 *
 * The floor and the free-trial flag both come from the API's `renewal` policy
 * (MobileSubscriptionAccessService::renewalPolicyForUser), so every picker agrees.
 *
 * @param {object}   options
 * @param {Array}    options.plans                 Rows from the plans endpoint.
 * @param {object}   options.renewal               The API's `renewal` policy, if present.
 * @param {boolean}  options.canStartFreeTrial     Fallback when there is no policy. Undefined
 *                                                 means "don't filter".
 * @param {number}   options.currentPlanPricePence Fallback floor; 0 for no plan.
 * @param {boolean}  options.allowDowngrade        Escape hatch for screens that must show all.
 */
export const offeredPlans = ({
  plans = [],
  renewal = null,
  canStartFreeTrial,
  currentPlanPricePence = 0,
  allowDowngrade = false,
} = {}) => {
  const allowFree =
    renewal && renewal.allow_free_plans !== undefined
      ? Boolean(renewal.allow_free_plans)
      : canStartFreeTrial;

  const floor = Math.max(
    Number(renewal?.minimum_price_pence || 0),
    Number(currentPlanPricePence || 0)
  );

  // After a free plan ends the list starts at Standard: cheaper rows (and the weekly taster)
  // are not shown at all rather than greyed.
  const hideBelowFloor = renewal?.reason === "trial_ended" || renewal?.reason === "on_trial";

  return (Array.isArray(plans) ? plans : [])
    .filter((plan) => {
      // An admin allow-listed this hidden plan (Weekly). It stays on the list.
      if (plan?.privately_offered) return true;
      return (
        !hideBelowFloor ||
        (!isFreePlan(plan) &&
          plan?.slug !== "trial-weekly" &&
          Number(plan?.price_pence || 0) >= floor)
      );
    })
    .map((plan) => {
    let blockedReason = null;

    if (plan?.slug === "trial-weekly" && allowFree === false && !plan?.privately_offered) {
      // The £1 weekly taster is a one-time offer like the free trial: greyed once used,
      // with the same ended-trial wording, never "lower than your previous plan".
      // privately_offered means an admin put this account on the Weekly allow-list,
      // which is permission to subscribe.
      blockedReason = "Your Weekly trial has ended. It can only be taken once.";
    } else if (isFreePlan(plan)) {
      // Undefined means the caller has no answer yet — offer it rather than blocking a real option.
      if (allowFree === false) {
        blockedReason = "You have already used the free trial.";
      }
    } else if (!plan?.privately_offered && !allowDowngrade && floor > 0 && Number(plan?.price_pence || 0) < floor) {
      // Keep the plan they are on selectable: it is their selection, and blocking it is disorienting.
      blockedReason = "Lower than your previous plan.";
    }

    return { ...plan, selectable: blockedReason === null, blockedReason };
  });
};

/**
 * Just the plans that can actually be chosen.
 *
 * Kept so callers that only ever wanted the choosable set stay unchanged. Screens
 * that show the blocked rows greyed out (with `blockedReason` as the caption) should
 * use {@link offeredPlans} instead — hiding the free trial outright left a returning
 * customer wondering where it went, and hid the reason along with it.
 */
export const selectablePlans = (options) =>
  offeredPlans(options).filter((plan) => plan.selectable);

/**
 * Which plan should start selected.
 *
 * For someone whose plan expired, that is the plan they were on — renewing should be one
 * click, not a re-decision. Otherwise fall back to whatever the caller preferred.
 */
export const defaultPlanId = ({ plans = [], renewal = null, preferredPlanId = null } = {}) => {
  const rows = Array.isArray(plans) ? plans : [];
  const has = (id) => rows.some((p) => Number(p.id) === Number(id));

  if (renewal?.default_plan_id && has(renewal.default_plan_id)) {
    return Number(renewal.default_plan_id);
  }
  if (preferredPlanId && has(preferredPlanId)) return Number(preferredPlanId);

  // Nothing is preselected otherwise (Standard is never assumed): the user chooses.
  return null;
};

/**
 * True when this plan is the paid plan the viewer actually held and is renewing — the only
 * row that may say "Your previous plan".
 *
 * `default_plan_id` alone is just the plan to preselect: after a free trial, the £1 weekly
 * taster or a cancellation it is Monthly, a plan they never had. So this also needs renew mode
 * for an expired or failed plan, and the plan they held (`previous_plan_id`).
 */
export const isRenewalPlan = (plan, renewal) =>
  renewal?.mode === "renew" &&
  ["expired", "payment_failed"].includes(renewal?.reason) &&
  plan?.slug !== "trial-weekly" &&
  !isFreePlan(plan) &&
  Number(plan?.id) === Number(renewal?.previous_plan_id ?? renewal?.default_plan_id);

/** The plan to recommend when choosing (Monthly after a trial or a cancellation). */
export const isRecommendedPlan = (plan, renewal) =>
  renewal?.mode === "choose" &&
  Boolean(renewal?.default_plan_id) &&
  Number(plan?.id) === Number(renewal.default_plan_id);

/** Headline for a returning customer, or null when they are simply choosing. */
export const renewalHeadline = (renewal) => {
  switch (renewal?.reason) {
    case "payment_failed":
      return "We could not charge your card";
    case "expired":
      return "Your plan has ended — renew it";
    case "cancelled":
      return "Choose a plan to start again";
    case "on_trial":
      return "Your free trial is still running — choose a plan to keep access";
    case "trial_ended":
      return "Your trial has ended — choose a plan";
    default:
      return null;
  }
};
